import React, { useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import AnnouncementPopup from "@/components/AnnouncementPopup";
import LayersHero from "@/components/LayersHero";
import { useSiteContent } from "@/context/SiteContentContext";
import { LogOut, Package, ScrollText, Users, HardDrive, MessageSquare, Settings, ListChecks, Euro, Menu, X, SlidersHorizontal, CheckCircle2 } from "lucide-react";

const LOGO = "https://customer-assets-jt897jd0.emergentagent.net/job_lab-portal-45/artifacts/5i852irv_logo%203D%20print.png";

export default function DashboardLayout({ title, subtitle, actions, children }) {
  const { user, logout } = useAuth();
  const { content, resolveImg } = useSiteContent();
  const g = content.general || {};
  const panelLogo = resolveImg(g.logo) || LOGO;
  const panelWatermark = g.panel_watermark_mode === "logo" ? panelLogo : g.panel_watermark_mode === "custom" && g.panel_watermark ? resolveImg(g.panel_watermark) : LOGO;
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const ordersPath = user?.role === "client" ? "/dashboard" : "/admin";
  const isAdmin = user?.role === "admin";
  const isStaff = user?.role === "admin" || user?.role === "operator";

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const links = [
    { to: ordersPath, label: "Ordini", icon: Package, testid: "nav-orders", show: true },
    { to: "/completati", label: "Completati", icon: CheckCircle2, testid: "nav-completati", show: isStaff },
    { to: "/archivio", label: "Archivio STL", icon: HardDrive, testid: "nav-archivio", show: isStaff },
    { to: "/listino", label: "Listino", icon: ScrollText, testid: "nav-listino", show: true },
    { to: "/impostazioni-lavori", label: "Impostazioni", icon: SlidersHorizontal, testid: "nav-impostazioni", show: isAdmin },
    { to: "/clienti", label: "Clienti", icon: Users, testid: "nav-clienti", show: isStaff },
    { to: "/messaggi", label: "Messaggi", icon: MessageSquare, testid: "nav-messaggi", show: isAdmin },
    { to: "/gestione", label: "Gestione Sito", icon: Settings, testid: "nav-gestione", show: isAdmin },
    { to: "/opzioni", label: "Opzioni", icon: ListChecks, testid: "nav-opzioni", show: isAdmin },
  ].filter((l) => l.show);

  const linkCls = ({ isActive }) =>
    `inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
      isActive ? "bg-teal-600 text-white shadow-sm" : "text-slate-600 hover:text-teal-700 hover:bg-teal-50"
    }`;

  const mobileLinkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-lg text-base font-semibold transition-colors ${
      isActive ? "bg-teal-600 text-white" : "text-slate-700 hover:bg-teal-50"
    }`;

  return (
    <div className="relative min-h-screen bg-[#171e25] overflow-hidden">
      <LayersHero fixed />
      {user?.role === "client" && <AnnouncementPopup />}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-[1] flex items-center justify-center">
        <img src={panelWatermark} alt="" data-testid="panel-watermark" className="w-[180%] max-w-none opacity-20 object-contain select-none" />
      </div>
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 lg:gap-6 min-w-0">
            <img src={panelLogo} alt="Officina Dentale 3D" data-testid="panel-logo" className="h-9 sm:h-10 object-contain cursor-pointer shrink-0" onClick={() => navigate(ordersPath)} />
            <nav className="hidden min-[1650px]:flex items-center gap-0.5">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} data-testid={l.testid} className={linkCls}>
                  <l.icon className="h-4 w-4 shrink-0" /> {l.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Button variant="outline" size="sm" data-testid="logout-button" onClick={doLogout}>
              <LogOut className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Esci</span>
            </Button>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setMenuOpen((v) => !v)}
              className="min-[1650px]:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
              aria-label="Menu"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="min-[1650px]:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
            <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} data-testid={`mobile-${l.testid}`} onClick={() => setMenuOpen(false)} className={mobileLinkCls}>
                  <l.icon className="h-5 w-5 shrink-0" /> {l.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-white">{title}</h1>
            {subtitle && <p className="text-slate-300 mt-1 text-sm sm:text-base">{subtitle}</p>}
          </div>
          {actions}
        </div>
        {children}
      </main>
    </div>
  );
}
